import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, User, ArrowRight, Loader2 } from 'lucide-react';
import { sharedStyles, createGlassCard, createButton } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

/**
 * UserTypeSelection - First-time user flow to choose podcaster or guest path
 *
 * Shown when a user has no shows AND no guest profile (new user).
 * Sets user_type in backend and redirects to appropriate onboarding.
 */
const UserTypeSelection = () => {
  const navigate = useNavigate();
  const { makeRequest } = useApi();
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const handleSelection = async (userType) => {
    setSelectedType(userType);
    setLoading(true);

    try {
      // Update user type in backend
      await makeRequest('users/me', {
        method: 'PATCH',
        body: JSON.stringify({ user_type: userType })
      });

      // Redirect to appropriate onboarding
      if (userType === 'podcaster') {
        navigate('/intake-form');
      } else {
        navigate('/guest/intake-form');
      }
    } catch (error) {
      console.error('Error setting user type:', error);
      setLoading(false);
      setSelectedType(null);
    }
  };

  const cardStyle = (type) => ({
    ...createGlassCard(),
    padding: '40px',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    border: selectedType === type
      ? `2px solid ${sharedStyles.colors.primary}`
      : '2px solid transparent',
    transform: selectedType === type ? 'scale(1.02)' : 'scale(1)',
    opacity: loading && selectedType !== type ? 0.5 : 1,
    flex: 1,
    maxWidth: '400px',
    textAlign: 'center',
  });

  const iconContainerStyle = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${sharedStyles.colors.primary}20, ${sharedStyles.colors.accent}20)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: sharedStyles.colors.background,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '600px' }}>
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: sharedStyles.colors.text,
          marginBottom: '16px',
        }}>
          Welcome to Conviviality
        </h1>
        <p style={{
          fontSize: '1.25rem',
          color: sharedStyles.colors.textSecondary,
          lineHeight: '1.6',
        }}>
          How would you like to use our platform?
        </p>
      </div>

      {/* Selection Cards */}
      <div style={{
        display: 'flex',
        gap: '32px',
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: '900px',
        width: '100%',
      }}>
        {/* Podcaster Card */}
        <div
          style={cardStyle('podcaster')}
          onClick={() => !loading && handleSelection('podcaster')}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            if (!loading && selectedType !== 'podcaster') {
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          <div style={iconContainerStyle}>
            {loading && selectedType === 'podcaster' ? (
              <Loader2 size={40} color={sharedStyles.colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Mic size={40} color={sharedStyles.colors.primary} />
            )}
          </div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: sharedStyles.colors.text,
            marginBottom: '12px',
          }}>
            I'm a Podcaster
          </h2>
          <p style={{
            fontSize: '1rem',
            color: sharedStyles.colors.textSecondary,
            lineHeight: '1.6',
            marginBottom: '24px',
          }}>
            Find amazing guests for your podcast. Get AI-powered recommendations
            matched to your show's topics and audience.
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            color: sharedStyles.colors.primary,
            fontWeight: '500',
          }}>
            <span>Find Guests</span>
            <ArrowRight size={18} />
          </div>
        </div>

        {/* Guest Card */}
        <div
          style={cardStyle('guest')}
          onClick={() => !loading && handleSelection('guest')}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            if (!loading && selectedType !== 'guest') {
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          <div style={iconContainerStyle}>
            {loading && selectedType === 'guest' ? (
              <Loader2 size={40} color={sharedStyles.colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <User size={40} color={sharedStyles.colors.primary} />
            )}
          </div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: sharedStyles.colors.text,
            marginBottom: '12px',
          }}>
            I'm a Potential Guest
          </h2>
          <p style={{
            fontSize: '1rem',
            color: sharedStyles.colors.textSecondary,
            lineHeight: '1.6',
            marginBottom: '24px',
          }}>
            Discover podcasts looking for guests like you. Get matched with
            shows that align with your expertise and interests.
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            color: sharedStyles.colors.primary,
            fontWeight: '500',
          }}>
            <span>Find Shows</span>
            <ArrowRight size={18} />
          </div>
        </div>
      </div>

      {/* Footer note */}
      <p style={{
        marginTop: '48px',
        fontSize: '0.875rem',
        color: sharedStyles.colors.textMuted,
        textAlign: 'center',
      }}>
        You can always switch or add the other role later from your settings.
      </p>

      {/* Keyframe for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default UserTypeSelection;
